/**
 * Scrape Recommendations Component
 * Displays prioritized scrape suggestions with smart recommendations
 */

import React from 'react';
import { RefreshCw, AlertCircle, Calendar, TrendingUp, Zap } from 'lucide-react';

/**
 * Format currency value
 */
const formatNumber = (value) => {
  if (value == null) return '0';
  return parseInt(value, 10).toLocaleString();
};

/**
 * Get priority color based on score
 */
const getPriorityColor = (score) => {
  if (score >= 80) return 'var(--color-error, #ef4444)';
  if (score >= 60) return 'var(--color-orange, #f97316)';
  if (score >= 40) return 'var(--color-warning, #f59e0b)';
  return 'var(--color-success, #10b981)';
};

/**
 * Priority Badge Component
 */
const PriorityBadge = ({ score }) => (
  <div
    className="dga-priority-badge"
    style={{ '--priority-color': getPriorityColor(score) }}
  >
    <div className="dga-priority-score">{score}</div>
    <div className="dga-priority-label">Priority</div>
  </div>
);

/**
 * Recommendation Card Component
 */
const RecommendationCard = ({ suggestion, onScrape, rank }) => {
  const {
    department_id,
    department_name,
    acronym,
    priority_score,
    reason,
    suggested_date_from,
    suggested_date_to,
    estimated_records,
    gap_ids
  } = suggestion;

  return (
    <div className="dga-recommendation-card">
      <div className="dga-recommendation-rank">#{rank}</div>

      <div className="dga-recommendation-main">
        <div className="dga-recommendation-header">
          <div className="dga-recommendation-dept">
            <span className="dga-recommendation-acronym">{acronym || '—'}</span>
            <span className="dga-recommendation-name">{department_name}</span>
          </div>
          <PriorityBadge score={priority_score} />
        </div>

        <div className="dga-recommendation-reason">
          <AlertCircle size={14} />
          <span>{reason}</span>
        </div>

        <div className="dga-recommendation-details">
          <div className="dga-recommendation-detail">
            <Calendar size={14} />
            <span>
              {new Date(suggested_date_from).toLocaleDateString()} - {new Date(suggested_date_to).toLocaleDateString()}
            </span>
          </div>
          <div className="dga-recommendation-detail">
            <TrendingUp size={14} />
            <span>~{formatNumber(estimated_records)} records expected</span>
          </div>
          {gap_ids && gap_ids.length > 0 && (
            <div className="dga-recommendation-detail">
              <Zap size={14} />
              <span>{gap_ids.length} gap{gap_ids.length > 1 ? 's' : ''} to fill</span>
            </div>
          )}
        </div>
      </div>

      <div className="dga-recommendation-action">
        <button
          className="dga-btn dga-btn-primary"
          onClick={() => onScrape({ department_id, suggested_date_from, suggested_date_to })}
        >
          <RefreshCw size={14} />
          Scrape
        </button>
      </div>

      <style>{`
        .dga-recommendation-card {
          display: flex;
          align-items: stretch;
          background: var(--color-bg-secondary, #1a1a1a);
          border: 1px solid var(--color-border, #333);
          border-radius: 12px;
          overflow: hidden;
        }

        .dga-recommendation-rank {
          width: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-text-tertiary, #666);
          background: var(--color-bg-tertiary, #252525);
          flex-shrink: 0;
        }

        .dga-recommendation-main {
          flex: 1;
          padding: 16px 20px;
          min-width: 0;
        }

        .dga-recommendation-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .dga-recommendation-dept {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .dga-recommendation-acronym {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-primary, #6366f1);
          background: rgba(99, 102, 241, 0.1);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .dga-recommendation-name {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
        }

        .dga-priority-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 4px 12px;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.2);
          border: 2px solid var(--priority-color);
        }

        .dga-priority-score {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--priority-color);
          line-height: 1;
        }

        .dga-priority-label {
          font-size: 0.625rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-tertiary, #666);
        }

        .dga-recommendation-reason {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 6px;
          margin-bottom: 12px;
        }

        .dga-recommendation-reason svg {
          color: var(--color-error, #ef4444);
          flex-shrink: 0;
        }

        .dga-recommendation-reason span {
          font-size: 0.8125rem;
          color: var(--color-text-secondary, #888);
        }

        .dga-recommendation-details {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }

        .dga-recommendation-detail {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8125rem;
          color: var(--color-text-tertiary, #666);
        }

        .dga-recommendation-detail svg {
          color: var(--color-text-secondary, #888);
        }

        .dga-recommendation-action {
          display: flex;
          align-items: center;
          padding: 16px;
          background: var(--color-bg-tertiary, #252525);
        }
      `}</style>
    </div>
  );
};

/**
 * Scrape Recommendations Component
 */
const ScrapeRecommendations = ({ suggestions, onNavigateToScraper }) => {
  if (!suggestions || suggestions.length === 0) {
    return (
      <div className="dga-recommendations-empty">
        <RefreshCw size={48} />
        <h3>All Caught Up!</h3>
        <p>No scrape recommendations at this time. Data is current across departments.</p>

        <style>{`
          .dga-recommendations-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 20px;
            text-align: center;
            color: var(--color-text-secondary, #888);
            background: var(--color-bg-secondary, #1a1a1a);
            border: 1px solid var(--color-border, #333);
            border-radius: 12px;
          }

          .dga-recommendations-empty svg {
            margin-bottom: 16px;
            color: var(--color-success, #10b981);
          }

          .dga-recommendations-empty h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--color-text-primary, #fff);
            margin: 0 0 8px;
          }

          .dga-recommendations-empty p {
            font-size: 0.875rem;
            color: var(--color-text-tertiary, #666);
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  const handleScrape = (params) => {
    onNavigateToScraper?.({
      department_id: params.department_id,
      date_from: params.suggested_date_from,
      date_to: params.suggested_date_to
    });
  };

  return (
    <div className="dga-recommendations">
      <div className="dga-recommendations-header">
        <h3>Smart Scrape Recommendations</h3>
        <p>Prioritized by data freshness, department importance, and gap severity</p>
      </div>

      <div className="dga-recommendations-list">
        {suggestions.map((suggestion, index) => (
          <RecommendationCard
            key={suggestion.department_id}
            suggestion={suggestion}
            onScrape={handleScrape}
            rank={index + 1}
          />
        ))}
      </div>

      <style>{`
        .dga-recommendations {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .dga-recommendations-header {
          padding: 0 4px;
        }

        .dga-recommendations-header h3 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
          margin: 0 0 4px;
        }

        .dga-recommendations-header p {
          font-size: 0.8125rem;
          color: var(--color-text-tertiary, #666);
          margin: 0;
        }

        .dga-recommendations-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
      `}</style>
    </div>
  );
};

export default ScrapeRecommendations;
